import React, { useMemo, useState } from 'react';
import {
  Box,
  Grid,
  Paper,
  Typography,
  CircularProgress,
  LinearProgress,
  Chip,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  TextField,
  Button,
  InputAdornment,
  useMediaQuery,
  useTheme
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import { Column, Bar, Pie } from '@ant-design/charts';
import moment from 'moment';

// 類別色（固定順序，不循環；超過 8 類由後端合併為「其他」）
const SERIES = ['#2a78d6', '#eb6834', '#1baf7a', '#eda100', '#e87ba4', '#008300', '#4a3aa7', '#e34948'];
const OTHER_COLOR = '#a3a29c';
const INK = { primary: '#0b0b0b', secondary: '#52514e', muted: '#8a8984', grid: '#e8e7e3' };

const colorFor = (data) => data.map((d, i) => (d.type === '其他' ? OTHER_COLOR : SERIES[i % SERIES.length]));
const fmtPct = (n, total) => (total > 0 ? `${Math.round((n / total) * 1000) / 10}%` : '0%');

function Section({ title, subtitle, children, action }) {
  return (
    <Paper variant="outlined" sx={{ p: { xs: 1.5, sm: 2.5 }, borderRadius: 3, height: '100%', boxSizing: 'border-box' }}>
      <Stack direction="row" alignItems="flex-start" justifyContent="space-between" spacing={1} sx={{ mb: 1.5 }}>
        <Box sx={{ minWidth: 0 }}>
          <Typography variant="subtitle1" sx={{ fontWeight: 700, lineHeight: 1.3 }}>{title}</Typography>
          {subtitle && (
            <Typography variant="caption" sx={{ color: INK.secondary, display: 'block', mt: 0.25 }}>{subtitle}</Typography>
          )}
        </Box>
        {action}
      </Stack>
      {children}
    </Paper>
  );
}

function StatTile({ label, value, suffix, hint, accent = SERIES[0], progress }) {
  return (
    <Paper variant="outlined" sx={{ p: { xs: 1.5, sm: 2 }, borderRadius: 3, height: '100%', boxSizing: 'border-box', borderTop: `4px solid ${accent}` }}>
      <Typography variant="body2" sx={{ color: INK.secondary, fontWeight: 500 }}>{label}</Typography>
      <Typography sx={{ fontSize: { xs: 26, sm: 32 }, fontWeight: 700, color: INK.primary, lineHeight: 1.2, mt: 0.5, fontVariantNumeric: 'tabular-nums' }}>
        {value}
        {suffix && <Typography component="span" sx={{ fontSize: 15, fontWeight: 500, color: INK.secondary, ml: 0.5 }}>{suffix}</Typography>}
      </Typography>
      {progress !== undefined && (
        <LinearProgress
          variant="determinate"
          value={Math.min(progress, 100)}
          sx={{ mt: 1, height: 6, borderRadius: 3, bgcolor: INK.grid, '& .MuiLinearProgress-bar': { bgcolor: accent, borderRadius: 3 } }}
        />
      )}
      {hint && <Typography variant="caption" sx={{ color: INK.muted, display: 'block', mt: 0.75 }}>{hint}</Typography>}
    </Paper>
  );
}

function Empty({ text }) {
  return (
    <Box sx={{ textAlign: 'center', py: 6 }}>
      <Typography sx={{ color: INK.muted }}>{text}</Typography>
    </Box>
  );
}

// 甜甜圈圖 + 自訂圖例（名稱／人數／百分比都以文字呈現，不只靠顏色辨識）
function DonutChart({ data, centerLabel = '人', emptyText = '暫無數據' }) {
  const total = data.reduce((s, d) => s + d.value, 0);
  if (!data.length || total === 0) return <Empty text={emptyText} />;
  const colors = colorFor(data);
  const config = {
    data,
    angleField: 'value',
    colorField: 'type',
    height: 220,
    autoFit: true,
    radius: 0.95,
    innerRadius: 0.62,
    scale: { color: { domain: data.map(d => d.type), range: colors } },
    legend: false,
    label: false,
    style: { stroke: '#ffffff', lineWidth: 2 },
    tooltip: {
      title: (d) => d.type,
      items: [(d) => ({ name: '人數', value: `${d.value} 人（${fmtPct(d.value, total)}）` })]
    },
    interaction: { elementHighlight: true }
  };
  return (
    <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, alignItems: 'center', gap: { xs: 1, sm: 2 } }}>
      <Box sx={{ position: 'relative', width: { xs: '100%', sm: '52%' }, maxWidth: { xs: 240, sm: 280 }, flexShrink: 0 }}>
        <Pie {...config} />
        <Box sx={{ position: 'absolute', inset: 0, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', pointerEvents: 'none' }}>
          <Typography sx={{ fontSize: 26, fontWeight: 700, color: INK.primary, lineHeight: 1.1, fontVariantNumeric: 'tabular-nums' }}>{total}</Typography>
          <Typography variant="caption" sx={{ color: INK.secondary }}>{centerLabel}</Typography>
        </Box>
      </Box>
      <Box component="ul" sx={{ listStyle: 'none', m: 0, p: 0, width: '100%', minWidth: 0 }}>
        {data.map((d, i) => (
          <Box component="li" key={d.type} sx={{ display: 'flex', alignItems: 'center', gap: 1, py: 0.6, borderBottom: i < data.length - 1 ? `1px solid ${INK.grid}` : 'none' }}>
            <Box sx={{ width: 12, height: 12, borderRadius: '3px', bgcolor: colors[i], flexShrink: 0 }} />
            <Typography variant="body2" sx={{ color: INK.primary, flex: 1, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={d.type}>
              {d.type}
            </Typography>
            <Typography variant="body2" sx={{ color: INK.primary, fontWeight: 600, fontVariantNumeric: 'tabular-nums' }}>{d.value}</Typography>
            <Typography variant="body2" sx={{ color: INK.secondary, width: 52, textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>{fmtPct(d.value, total)}</Typography>
          </Box>
        ))}
      </Box>
    </Box>
  );
}

function TrendChart({ data, unit }) {
  if (!data.length) return <Empty text="暫無報名數據" />;
  const label = (v) => (unit === 'week' ? `${moment(v).format('MM/DD')}週` : moment(v).format('MM/DD'));
  const config = {
    data,
    xField: 'date',
    yField: 'count',
    height: 240,
    autoFit: true,
    style: { fill: SERIES[0], radiusTopLeft: 4, radiusTopRight: 4, maxWidth: 24 },
    axis: {
      x: { title: false, labelFormatter: label, labelAutoHide: true, labelAutoRotate: false, line: true, lineStroke: INK.grid, tick: false },
      y: { title: false, gridStroke: INK.grid, gridStrokeOpacity: 1, gridLineDash: [0, 0], labelFill: INK.secondary }
    },
    tooltip: {
      title: (d) => (unit === 'week' ? `${d.date} 當週` : moment(d.date).format('YYYY/MM/DD (dd)')),
      items: [
        (d) => ({ name: unit === 'week' ? '當週報名' : '當日報名', value: `${d.count} 人`, color: SERIES[0] }),
        (d) => ({ name: '累計報名', value: `${d.cumulative} 人`, color: INK.secondary })
      ]
    }
  };
  return <Column {...config} />;
}

function MonthlyStackChart({ data }) {
  if (!data.length) return <Empty text="近三個月無新加入使用者" />;
  const config = {
    data,
    xField: 'month',
    yField: 'value',
    colorField: 'type',
    stack: true,
    height: 240,
    autoFit: true,
    scale: { color: { domain: ['已報名本活動', '未報名'], range: [SERIES[0], SERIES[1]] } },
    style: { maxWidth: 56, stroke: '#ffffff', lineWidth: 2 },
    legend: { color: { position: 'top', layout: { justifyContent: 'center' }, itemLabelFill: INK.primary } },
    axis: {
      x: { title: false, labelFormatter: (v) => `${Number(v.slice(5))}月`, tick: false, lineStroke: INK.grid },
      y: { title: false, gridStroke: INK.grid, gridStrokeOpacity: 1, gridLineDash: [0, 0], labelFill: INK.secondary }
    },
    tooltip: { items: [(d) => ({ name: d.type, value: `${d.value} 人` })] }
  };
  return <Column {...config} />;
}

function DepartmentBarChart({ data }) {
  if (!data.length) return <Empty text="暫無 IMC 社別資料" />;
  const config = {
    data,
    xField: 'type',
    yField: 'value',
    height: Math.max(160, data.length * 30 + 30),
    paddingRight: 36,
    autoFit: true,
    style: { fill: SERIES[2], radiusTopLeft: 4, radiusTopRight: 4, maxWidth: 18 },
    label: { text: 'value', position: 'right', dx: 6, style: { fill: INK.primary, fontWeight: 600 } },
    axis: {
      x: { title: false, tick: false, line: false, labelFill: INK.primary },
      y: false
    },
    tooltip: { items: [(d) => ({ name: '報名人數', value: `${d.value} 人` })] }
  };
  return <Bar {...config} />;
}

function exportCsv(rows, filename) {
  const header = ['加入日期', 'LINE 名稱', '真實姓名', '社團', '社別', '曾報名其他活動'];
  const esc = (v) => `"${String(v ?? '').replace(/"/g, '""')}"`;
  const lines = rows.map(u => [
    moment(u.createdAt).format('YYYY-MM-DD HH:mm'), u.displayName, u.realName, u.community, u.department, u.registeredOther ? '是' : '否'
  ].map(esc).join(','));
  const blob = new Blob(['﻿' + [header.map(esc).join(','), ...lines].join('\r\n')], { type: 'text/csv;charset=utf-8' });
  const a = document.createElement('a');
  a.href = URL.createObjectURL(blob);
  a.download = filename;
  a.click();
  URL.revokeObjectURL(a.href);
}

function UnregisteredTable({ users, productName }) {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const [keyword, setKeyword] = useState('');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const filtered = useMemo(() => {
    const k = keyword.trim().toLowerCase();
    if (!k) return users;
    return users.filter(u => [u.displayName, u.realName, u.community, u.department].some(v => (v || '').toLowerCase().includes(k)));
  }, [users, keyword]);
  const rows = filtered.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);

  return (
    <Box>
      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} sx={{ mb: 1 }}>
        <TextField
          size="small"
          placeholder="搜尋名稱／姓名／社團"
          value={keyword}
          onChange={(e) => { setKeyword(e.target.value); setPage(0); }}
          sx={{ flex: 1 }}
          slotProps={{ input: { startAdornment: <InputAdornment position="start"><SearchIcon fontSize="small" /></InputAdornment> } }}
        />
        <Button
          variant="outlined"
          size="small"
          startIcon={<FileDownloadIcon />}
          onClick={() => exportCsv(filtered, `${productName}_近三個月未報名_${moment().format('YYYYMMDD')}.csv`)}
          disabled={!filtered.length}
        >
          匯出 CSV（{filtered.length}）
        </Button>
      </Stack>
      <TableContainer sx={{ border: `1px solid ${INK.grid}`, borderRadius: 2 }}>
        <Table size="small">
          <TableHead>
            <TableRow sx={{ '& th': { fontWeight: 700, color: INK.secondary, whiteSpace: 'nowrap', bgcolor: '#f7f7f5' } }}>
              <TableCell>加入日期</TableCell>
              <TableCell>名稱</TableCell>
              {!isMobile && <TableCell>社團／社別</TableCell>}
              <TableCell align="right">其他活動</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {rows.map(u => (
              <TableRow key={u.id} hover>
                <TableCell sx={{ whiteSpace: 'nowrap', fontVariantNumeric: 'tabular-nums' }}>{moment(u.createdAt).format(isMobile ? 'MM/DD' : 'YYYY/MM/DD')}</TableCell>
                <TableCell sx={{ maxWidth: 180 }}>
                  <Typography variant="body2" noWrap>{u.displayName || '-'}</Typography>
                  {(u.realName || isMobile) && (
                    <Typography variant="caption" sx={{ color: INK.secondary }} noWrap component="div">
                      {[u.realName, isMobile ? u.community : null].filter(Boolean).join('・') || ' '}
                    </Typography>
                  )}
                </TableCell>
                {!isMobile && <TableCell>{[u.community, u.department].filter(Boolean).join(' / ') || <span style={{ color: INK.muted }}>未填寫</span>}</TableCell>}
                <TableCell align="right">
                  {u.registeredOther
                    ? <Chip label="曾報名" size="small" variant="outlined" />
                    : <Typography variant="caption" sx={{ color: INK.muted }}>從未報名</Typography>}
                </TableCell>
              </TableRow>
            ))}
            {!rows.length && (
              <TableRow><TableCell colSpan={4}><Empty text="沒有符合的使用者" /></TableCell></TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>
      <TablePagination
        component="div"
        count={filtered.length}
        page={page}
        onPageChange={(e, p) => setPage(p)}
        rowsPerPage={rowsPerPage}
        onRowsPerPageChange={(e) => { setRowsPerPage(parseInt(e.target.value, 10)); setPage(0); }}
        rowsPerPageOptions={[10, 25, 50]}
        labelRowsPerPage={isMobile ? '' : '每頁'}
        labelDisplayedRows={({ from, to, count }) => `${from}-${to} / ${count}`}
      />
    </Box>
  );
}

export default function ProductDashboard({ data, loading }) {
  if (loading) {
    return (
      <Box sx={{ textAlign: 'center', padding: 5 }}>
        <CircularProgress size={60} />
        <Typography variant="body1" sx={{ mt: 2 }}>
          載入儀表板數據中...
        </Typography>
      </Box>
    );
  }

  if (!data) {
    return <Empty text="無數據" />;
  }

  const { productInfo, statistics, acquisition, participation, recentUnregistered, charts } = data;
  const fmt = (d) => moment(d).format('YYYY/MM/DD HH:mm');
  const hasCheckins = statistics.checkinCount > 0;

  return (
    <Box sx={{ bgcolor: '#fafaf8', mx: { xs: -1, sm: 0 }, p: { xs: 1, sm: 2 }, borderRadius: { sm: 3 } }}>
      {/* 活動基本信息 */}
      <Box sx={{ mb: 2 }}>
        <Typography sx={{ fontSize: { xs: 18, sm: 22 }, fontWeight: 700, color: INK.primary }}>
          {`${productInfo.name} - 活動儀表板`}
        </Typography>
        <Stack direction="row" flexWrap="wrap" useFlexGap spacing={1} sx={{ mt: 1 }}>
          {productInfo.startTime && <Chip size="small" label={`開始 ${fmt(productInfo.startTime)}`} />}
          {productInfo.endTime && <Chip size="small" label={`結束 ${fmt(productInfo.endTime)}`} />}
          {productInfo.registrationEnd && <Chip size="small" variant="outlined" label={`報名截止 ${fmt(productInfo.registrationEnd)}`} />}
        </Stack>
      </Box>

      {/* 主要指標 */}
      <Grid container spacing={{ xs: 1.5, sm: 2 }} sx={{ mb: 2 }}>
        <Grid size={{ xs: 6, md: 3 }}>
          <StatTile label="有效報名" value={statistics.registrationCount} suffix="人"
            hint={`已確認 ${statistics.paidCount}・待確認 ${statistics.pendingCount}・取消 ${statistics.cancelledCount}`} />
        </Grid>
        <Grid size={{ xs: 6, md: 3 }}>
          <StatTile label="因本活動新加入" value={acquisition.newRegistrants} suffix="人" accent={SERIES[1]}
            progress={acquisition.newRegistrantShare}
            hint={`占報名者 ${acquisition.newRegistrantShare}%`} />
        </Grid>
        <Grid size={{ xs: 6, md: 3 }}>
          <StatTile label="回流參加者" value={participation.returningCount} suffix="人" accent={SERIES[2]}
            progress={participation.returningRate}
            hint={`第 2 次以上參加占 ${participation.returningRate}%`} />
        </Grid>
        <Grid size={{ xs: 6, md: 3 }}>
          <StatTile label="簽到" value={statistics.checkinCount} suffix="人" accent={SERIES[6]}
            progress={statistics.checkinRate}
            hint={hasCheckins ? `簽到率 ${statistics.checkinRate}%` : '活動尚未開始簽到'} />
        </Grid>
      </Grid>

      <Grid container spacing={{ xs: 1.5, sm: 2 }}>
        {/* 新加入分析 */}
        <Grid size={{ xs: 12, md: 6 }}>
          <Section
            title="因本活動加入的人數"
            subtitle={`活動上架（${moment(acquisition.windowStart).format('YYYY/MM/DD')}）後才加入 LINE 好友的使用者`}
          >
            <DonutChart
              centerLabel="位報名者"
              data={[
                { type: '活動期間新加入', value: acquisition.newRegistrants },
                { type: '原有好友', value: statistics.registrationCount - acquisition.newRegistrants }
              ]}
            />
            <Box sx={{ mt: 1.5, p: 1.5, borderRadius: 2, bgcolor: '#f3f2ef' }}>
              <Typography variant="body2" sx={{ color: INK.primary }}>
                活動期間共有 <b>{acquisition.newUsersInWindow}</b> 位新好友加入，其中 <b>{acquisition.newRegistrants}</b> 位報名本活動，
                轉換率 <b>{acquisition.conversionRate}%</b>。
              </Typography>
            </Box>
          </Section>
        </Grid>

        {/* 參加次數 */}
        <Grid size={{ xs: 12, md: 6 }}>
          <Section title="參加者第幾次參加" subtitle="依本活動開始前、其他活動的簽到紀錄計算（本次算 1 次）">
            <DonutChart data={participation.distribution} centerLabel="位報名者" />
          </Section>
        </Grid>

        {/* 報名趨勢 */}
        <Grid size={{ xs: 12, lg: 8 }}>
          <Section title={charts.trendUnit === 'week' ? '每週報名趨勢' : '每日報名趨勢'} subtitle="滑過長條可看當日與累計人數（台北時間）">
            <TrendChart data={charts.registrationTrend} unit={charts.trendUnit} />
          </Section>
        </Grid>

        {/* 報名者社團 */}
        <Grid size={{ xs: 12, lg: 4 }}>
          <Section title="報名者社團分佈" subtitle="以報名表填寫的社團為準">
            <DonutChart data={charts.registrantCommunity} centerLabel="位報名者" emptyText="暫無報名數據" />
          </Section>
        </Grid>

        {/* IMC 社別 */}
        <Grid size={{ xs: 12, md: hasCheckins ? 6 : 12 }}>
          <Section title="IMC 報名者社別" subtitle="前 12 名">
            <DepartmentBarChart data={charts.imcDepartments} />
          </Section>
        </Grid>

        {hasCheckins && (
          <Grid size={{ xs: 12, md: 6 }}>
            <Section title="簽到人員社團分佈">
              <DonutChart data={charts.communityDistribution} centerLabel="位簽到" />
            </Section>
          </Grid>
        )}

        {/* 近三個月加入未報名 */}
        <Grid size={12}>
          <Section
            title="近三個月加入、尚未報名本活動"
            subtitle={`${moment(recentUnregistered.since).format('YYYY/MM/DD')} 起加入的 LINE 好友（不含管理人員）`}
          >
            <Grid container spacing={{ xs: 1.5, sm: 2 }} sx={{ mb: 2 }}>
              <Grid size={{ xs: 6, md: 4 }}>
                <StatTile label="近三個月加入" value={recentUnregistered.joinedCount} suffix="人" />
              </Grid>
              <Grid size={{ xs: 6, md: 4 }}>
                <StatTile label="尚未報名本活動" value={recentUnregistered.count} suffix="人" accent={SERIES[1]}
                  progress={recentUnregistered.rate} hint={`占新加入 ${recentUnregistered.rate}%`} />
              </Grid>
              <Grid size={{ xs: 12, md: 4 }}>
                <StatTile label="從未報名任何活動" value={recentUnregistered.neverRegisteredAny} suffix="人" accent={SERIES[7]}
                  hint="適合再次推播邀請" />
              </Grid>
            </Grid>
            <Grid container spacing={{ xs: 1.5, sm: 2 }}>
              <Grid size={{ xs: 12, md: 7 }}>
                <Typography variant="body2" sx={{ fontWeight: 600, color: INK.secondary, mb: 1 }}>每月新加入：已報名 vs 未報名</Typography>
                <MonthlyStackChart data={recentUnregistered.monthly} />
              </Grid>
              <Grid size={{ xs: 12, md: 5 }}>
                <Typography variant="body2" sx={{ fontWeight: 600, color: INK.secondary, mb: 1 }}>未報名者社團</Typography>
                <DonutChart data={recentUnregistered.communityDistribution} centerLabel="位未報名" emptyText="全部都已報名" />
              </Grid>
              <Grid size={12}>
                <Typography variant="body2" sx={{ fontWeight: 600, color: INK.secondary, mb: 1 }}>未報名名單</Typography>
                <UnregisteredTable users={recentUnregistered.users} productName={productInfo.name} />
              </Grid>
            </Grid>
          </Section>
        </Grid>
      </Grid>
    </Box>
  );
}
